// Optional per-page CSS for Page Builder pages ("Custom CSS (this page only)" in Page Settings).
//
// A page's CSS is wrapped as `#pb-page-<id> { ... }` using native CSS nesting, so every rule in
// it can only match inside that page's own wrapper div (PageViewer sets the id). A page without
// custom CSS gets no <style> tag at all and renders exactly as before.
//
// @keyframes cannot be defined inside a style rule, so they are lifted out and placed before the
// wrapper. Their names are global; page CSS should prefix them (e.g. `pb-bsm2026-cta-glow`).
//
// Refused (nothing injected) when the braces do not balance: an extra `}` would close the wrapper
// early and let the rest of the CSS style other pages.
//
// Designed for the BSM 2026 agenda restyle (tasks/agenda-page/agenda-bsm2026/docs/17_RESTYLE_PLAN.md).

const stripCommentsAndStrings = (css) =>
  css
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/"(?:[^"\\\n]|\\.)*"|'(?:[^'\\\n]|\\.)*'/g, '""');

export const bracesBalance = (css) => {
  let depth = 0;
  for (const ch of stripCommentsAndStrings(css)) {
    if (ch === '{') depth += 1;
    else if (ch === '}') {
      depth -= 1;
      if (depth < 0) return false;
    }
  }
  return depth === 0;
};

const liftKeyframes = (css) => {
  const keyframes = [];
  let rest = '';
  let i = 0;
  const re = /@(-webkit-)?keyframes\s+[\w-]+\s*\{/g;
  let match;
  while ((match = re.exec(css)) !== null) {
    if (match.index < i) continue;
    let depth = 0;
    let j = match.index + match[0].length - 1;
    for (; j < css.length; j += 1) {
      if (css[j] === '{') depth += 1;
      else if (css[j] === '}') {
        depth -= 1;
        if (depth === 0) break;
      }
    }
    rest += css.slice(i, match.index);
    keyframes.push(css.slice(match.index, j + 1));
    i = j + 1;
    re.lastIndex = i;
  }
  rest += css.slice(i);
  return { keyframes, rest };
};

export const pageScopeId = (pageId) =>
  pageId === undefined || pageId === null ? undefined : `pb-page-${pageId}`;

// Returns the text for the page's <style> tag, or null when there is nothing (safe) to inject.
export const buildScopedCss = (pageId, customCss) => {
  const scope = pageScopeId(pageId);
  if (!scope || typeof customCss !== 'string' || customCss.trim() === '')
    return null;
  if (!bracesBalance(customCss)) return null;
  const { keyframes, rest } = liftKeyframes(customCss);
  return `${keyframes.join('\n')}\n#${scope} {\n${rest}\n}\n`;
};
